/**
 * Shared React authentication provider with multi-account support.
 *
 * Wraps any micro-frontend in an {@link AuthContext} that manages the full
 * auth lifecycle: token detection → session validation → scope resolution →
 * logout. Each zone (web, admin, student) mounts its own instance, but the
 * logic is shared here.
 *
 * ## Multi-account registry
 *
 * In addition to the single active session (`accessToken` / `refreshToken`),
 * the provider maintains a registry of saved accounts in `localStorage`.
 * Each entry snapshots the user's identity and their last-known refresh
 * token so the session can be resumed without re-authenticating via OTP.
 *
 * - On successful bootstrap, the active account is saved/updated in the
 *   registry so the refresh token stays in sync after rotations.
 * - **Switch Account** preserves the current session in the registry and
 *   navigates to `/login`, where saved accounts appear for one-click
 *   re-login.
 * - **Log Out** revokes the refresh token server-side and removes the
 *   account from the registry permanently.
 *
 * The provider makes direct `fetch` calls to the backend rather than going
 * through the oRPC client to avoid circular initialisation dependencies and
 * to keep the auth bootstrap as lean as possible.
 *
 * @module lib/auth-provider
 */

import {
	getSavedAccounts,
	removeAccount as removeAccountFromRegistry,
	type SavedAccount,
	saveAccount as saveAccountToRegistry,
} from '@api/lib/accounts';
import {
	type AuthScope,
	type AuthState,
	type AuthUser,
	decodeAccessToken,
	type EnrollmentEntry,
	type MembershipEntry,
	resolveScopes,
} from '@api/lib/auth';
import { storage } from '@api/lib/storage';
import { tryCatch } from '@learnai/tools/try-catch';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** How long to wait for any single auth-related fetch (ms). */
const AUTH_FETCH_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Extended auth state
// ---------------------------------------------------------------------------

/**
 * Full authentication context value including multi-account support.
 *
 * Extends the base {@link AuthState} (defined in `auth.ts`) with account-
 * switching primitives. Consumers receive this type via {@link useAuth}.
 */
interface AuthContextValue extends AuthState {
	/** All saved accounts in the multi-account registry. */
	savedAccounts: SavedAccount[];
	/**
	 * Preserve the current session in the saved-accounts registry and
	 * navigate to `/login` for account switching. The current account
	 * remains resumable from the login page's quick-login list.
	 */
	switchAccount: () => Promise<void>;
	/**
	 * Re-reads the saved-accounts registry from storage into React state.
	 *
	 * Call this after directly manipulating the registry via the
	 * `@learnai/api/accounts` module (e.g. removing a saved account on
	 * the login page) so the provider's `savedAccounts` array reflects
	 * the change.
	 */
	refreshSavedAccounts: () => Promise<void>;
	/**
	 * Re-fetches the current user from the server (GET /auth/session) and
	 * updates auth state. Call after updating the profile (e.g. avatar or
	 * name) so the nav/sidebar and other consumers of useAuth() show the
	 * new data without re-login.
	 */
	refreshUser: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Wrapper around `fetch` that attaches the access token and enforces a
 * timeout. Returns `null` on any failure so callers can degrade gracefully.
 */
async function authFetch<T>(path: string, accessToken: string): Promise<T | null> {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), AUTH_FETCH_TIMEOUT_MS);

	const { data: response, error: fetchError } = await tryCatch(
		fetch(path, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: 'application/json',
			},
			signal: controller.signal,
		}),
	);

	clearTimeout(timer);

	if (fetchError || !response || !response.ok) return null;

	const { data: json, error: jsonError } = await tryCatch(response.json() as Promise<T>);
	if (jsonError || !json) return null;

	return json;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
	children: React.ReactNode;
	/**
	 * Base URL for API requests (e.g. `""` for same-origin or
	 * `"http://localhost:3000"`). Defaults to same-origin.
	 */
	baseUrl?: string;
}

function AuthProvider({ children, baseUrl = '' }: AuthProviderProps) {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [scopes, setScopes] = useState<AuthScope[]>([]);
	const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	// Guard against double-execution in StrictMode.
	const initialised = useRef(false);

	// -------------------------------------------------------------------
	// Bootstrap — runs once on mount
	// -------------------------------------------------------------------

	useEffect(() => {
		if (initialised.current) return;
		initialised.current = true;

		(async () => {
			try {
				// Load saved-accounts registry immediately so it's
				// available by the time isLoading flips to false (the
				// login page renders saved accounts as soon as it mounts).
				setSavedAccounts(await getSavedAccounts());

				let accessToken = await storage.local.get('accessToken');

				if (!accessToken) {
					// No access token — check if we can silently refresh.
					const hasRefreshToken = await storage.local.get('refreshToken');
					if (!hasRefreshToken) return;

					const refreshed = await attemptRefresh(baseUrl);
					if (!refreshed) {
						await storage.local.delete('refreshToken');
						await storage.local.delete('currentSessionId');
						return;
					}

					accessToken = await storage.local.get('accessToken');
					if (!accessToken) return;
				}

				// ── Optimistic decode (instant, no network) ───────────
				const decoded = decodeAccessToken(accessToken);
				if (!decoded) {
					// Token is expired or malformed — attempt a refresh
					// before giving up.
					const refreshed = await attemptRefresh(baseUrl);
					if (!refreshed) {
						await storage.local.delete('accessToken');
						await storage.local.delete('refreshToken');
						await storage.local.delete('currentSessionId');
						return;
					}

					// Refresh succeeded — validate the new token and
					// resolve scopes, then bail out of the normal flow.
					const newToken = await storage.local.get('accessToken');
					if (newToken) {
						const freshSession = await authFetch<{ user: AuthUser }>(`${baseUrl}/api/auth/session`, newToken);
						if (freshSession) {
							setUser(freshSession.user);
							await fetchAndSetScopes(newToken, baseUrl, setScopes);
							await snapshotToRegistry(freshSession.user, setSavedAccounts);
							return;
						}
					}

					// New token also failed — clear everything.
					await storage.local.delete('accessToken');
					await storage.local.delete('refreshToken');
					await storage.local.delete('currentSessionId');
					return;
				}

				// Show user info immediately while we validate server-side.
				setUser(decoded);

				// ── Server validation ─────────────────────────────────
				const session = await authFetch<{ user: AuthUser }>(`${baseUrl}/api/auth/session`, accessToken);

				if (!session) {
					// Token rejected by server — could be revoked or the
					// server is unreachable. Attempt a refresh before
					// giving up.
					const refreshed = await attemptRefresh(baseUrl);
					if (!refreshed) {
						setUser(null);
						setScopes([]);
						await storage.local.delete('accessToken');
						await storage.local.delete('refreshToken');
						await storage.local.delete('currentSessionId');
						return;
					}

					// Refresh succeeded — use the new token for scope calls.
					const newToken = await storage.local.get('accessToken');
					if (newToken) {
						const freshSession = await authFetch<{ user: AuthUser }>(`${baseUrl}/api/auth/session`, newToken);
						if (freshSession) {
							setUser(freshSession.user);
							await fetchAndSetScopes(newToken, baseUrl, setScopes);
							await snapshotToRegistry(freshSession.user, setSavedAccounts);
						}
					}
					return;
				}

				// Update user with server-validated data.
				setUser(session.user);

				// ── Scope resolution ──────────────────────────────────
				await fetchAndSetScopes(accessToken, baseUrl, setScopes);

				// ── Snapshot to registry ──────────────────────────────
				await snapshotToRegistry(session.user, setSavedAccounts);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [baseUrl]);

	// -------------------------------------------------------------------
	// Logout — revoke token + remove from registry
	// -------------------------------------------------------------------

	const logout = useCallback(async () => {
		const accessToken = await storage.local.get('accessToken');
		const refreshToken = await storage.local.get('refreshToken');

		// Best-effort server-side revocation.
		if (accessToken && refreshToken) {
			await tryCatch(
				fetch(`${baseUrl}/api/auth/logout`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ refreshToken }),
				}),
			);
		}

		// Remove from saved-accounts registry (permanent sign-out).
		if (user) {
			await removeAccountFromRegistry(user.id);
			setSavedAccounts(await getSavedAccounts());
		}

		// Always clear client state regardless of server response.
		await storage.local.delete('accessToken');
		await storage.local.delete('refreshToken');
		await storage.local.delete('currentSessionId');
		setUser(null);
		setScopes([]);

		// Full-page redirect to the login page (cross-zone navigation).
		window.location.href = '/login';
	}, [baseUrl, user]);

	// -------------------------------------------------------------------
	// Refresh user — re-fetch session so auth state has latest profile
	// -------------------------------------------------------------------

	const refreshUser = useCallback(async () => {
		const accessToken = await storage.local.get('accessToken');
		if (!accessToken) return;
		const session = await authFetch<{ user: AuthUser }>(`${baseUrl}/api/auth/session`, accessToken);
		if (session) {
			setUser(session.user);
			await snapshotToRegistry(session.user, setSavedAccounts);
		}
	}, [baseUrl]);

	// -------------------------------------------------------------------
	// Switch account — save session + navigate to login
	// -------------------------------------------------------------------

	const switchAccount = useCallback(async () => {
		// Snapshot the current session into the registry so it can be
		// resumed from the login page later.
		const refreshToken = await storage.local.get('refreshToken');
		if (user && refreshToken) {
			await saveAccountToRegistry({
				id: user.id,
				email: user.email,
				name: user.name,
				avatarUrl: user.avatarUrl,
				refreshToken,
			});
		}

		// Clear active session tokens but NOT the saved-accounts
		// registry — the whole point is to preserve other sessions.
		await storage.local.delete('accessToken');
		await storage.local.delete('refreshToken');
		await storage.local.delete('currentSessionId');
		setUser(null);
		setScopes([]);

		// Full-page redirect to the login page where saved accounts
		// are displayed for quick re-login.
		window.location.href = '/login';
	}, [user]);

	// -------------------------------------------------------------------
	// Refresh saved accounts — re-read registry from storage
	// -------------------------------------------------------------------

	const refreshSavedAccounts = useCallback(async () => {
		setSavedAccounts(await getSavedAccounts());
	}, []);

	// -------------------------------------------------------------------
	// Context value
	// -------------------------------------------------------------------

	const hasScope = useCallback((scope: AuthScope) => scopes.includes(scope), [scopes]);

	const profileComplete = useMemo(() => {
		if (!user) return false;
		const hasName = typeof user.name === 'string' && user.name.trim().length > 0;
		const hasBirthday = typeof user.birthday === 'string' && user.birthday.length > 0;
		return hasName && hasBirthday;
	}, [user]);

	const value = useMemo<AuthContextValue>(
		() => ({
			user,
			scopes,
			isReady: !isLoading,
			isLoading,
			isAuthenticated: user !== null,
			profileComplete,
			hasScope,
			logout,
			savedAccounts,
			switchAccount,
			refreshSavedAccounts,
			refreshUser,
		}),
		[
			user,
			scopes,
			isLoading,
			profileComplete,
			hasScope,
			logout,
			savedAccounts,
			switchAccount,
			refreshSavedAccounts,
			refreshUser,
		],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the current {@link AuthContextValue} from the nearest
 * {@link AuthProvider}. Throws if used outside the provider tree.
 */
function useAuth(): AuthContextValue {
	const context = useContext(AuthContext);

	if (context === undefined) {
		throw new Error('useAuth must be used within an <AuthProvider>');
	}

	return context;
}

// ---------------------------------------------------------------------------
// Internal — registry snapshotting
// ---------------------------------------------------------------------------

/**
 * Saves/updates the given user in the multi-account registry with the
 * current refresh token from storage. Called at each successful
 * bootstrap exit so the registry stays in sync after token rotations.
 */
async function snapshotToRegistry(
	authUser: AuthUser,
	setSavedAccounts: React.Dispatch<React.SetStateAction<SavedAccount[]>>,
): Promise<void> {
	const refreshToken = await storage.local.get('refreshToken');
	if (!refreshToken) return;

	await saveAccountToRegistry({
		id: authUser.id,
		email: authUser.email,
		name: authUser.name,
		avatarUrl: authUser.avatarUrl,
		refreshToken,
	});

	// Re-read from storage so React state reflects the update.
	setSavedAccounts(await getSavedAccounts());
}

// ---------------------------------------------------------------------------
// Internal — scope fetching
// ---------------------------------------------------------------------------

async function fetchAndSetScopes(
	accessToken: string,
	baseUrl: string,
	setScopes: React.Dispatch<React.SetStateAction<AuthScope[]>>,
): Promise<void> {
	const [membershipsResult, enrollmentsResult] = await Promise.all([
		authFetch<{ memberships: MembershipEntry[] }>(`${baseUrl}/api/users/me/memberships`, accessToken),
		authFetch<{ enrollments: EnrollmentEntry[] }>(`${baseUrl}/api/users/me/enrollments`, accessToken),
	]);

	const memberships = membershipsResult?.memberships ?? [];
	const enrollments = enrollmentsResult?.enrollments ?? [];
	const resolved = resolveScopes(memberships, enrollments);

	setScopes(resolved);
}

// ---------------------------------------------------------------------------
// Internal — token refresh
// ---------------------------------------------------------------------------

async function attemptRefresh(baseUrl: string): Promise<boolean> {
	const refreshToken = await storage.local.get('refreshToken');
	if (!refreshToken) return false;

	const { data: response, error } = await tryCatch(
		fetch(`${baseUrl}/api/auth/refresh`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ refreshToken }),
		}),
	);

	if (error || !response || !response.ok) return false;

	const { data: body, error: jsonError } = await tryCatch(
		response.json() as Promise<{
			tokens: {
				accessToken: string;
				refreshToken: string;
				sessionId?: string;
			};
		}>,
	);

	if (jsonError || !body?.tokens?.accessToken) return false;

	await storage.local.set('accessToken', body.tokens.accessToken);
	await storage.local.set('refreshToken', body.tokens.refreshToken);
	if (body.tokens.sessionId) {
		await storage.local.set('currentSessionId', body.tokens.sessionId);
	}

	return true;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { AuthProvider, useAuth };
export type { AuthContextValue, AuthProviderProps, SavedAccount };
